<template>
  <div
    class="py-8 lg:py-12"
    :class="{
      'bg-dark text-white': background === 'dark',
      'bg-light-white': background === 'light',
    }"
    v-editable="blok"
  >
    <div class="2xl:container relative">
      <form
        class="grid mb-8 max-lg:grid-rows-3 lg:grid-cols-12 gap-y-3 lg:gap-x-12 lg:border-t-8 lg:border-t-primary-accent"
        :class="{
          container: width < 1024,
        }"
        @submit.prevent="searchEvents"
      >
        <div
          class="flex py-6 xs:gap-x-2 lg:col-span-4 grow border-t-8 border-primary-accent lg:border-0"
        >
          <MxField
            class="w-full flex items-center min-w-0 text-xl rounded-full bg-white pl-4 text-black pr-12 shadow-[inset_0_2px_4px_0_rgba(0,0,0,0.1)] focus:outline-none"
            v-model="searchData.term"
            id="eventSearch"
            :placeholder="translate('label.searchArtistEvent')"
          />

          <MxButton
            type="submit"
            :class="'shadow-[inset_10px_0_20px_-20px_rgba(0,0,0,0.5)] rounded-r-full -ml-12'"
          >
            <Icon class="text-2xl" name="Search" />
          </MxButton>
        </div>

        <div class="py-6 grow border-t-8 lg:col-span-4 border-primary-accent lg:border-0 lg:mr-1/5">
          <MxButton class="w-full h-full justify-between" @click="filtersOpen = !filtersOpen">
            {{ translate('label.filterByCategory') }}
            <Icon
              class="text-2xl transform transition-transform"
              :class="{
                'rotate-180': filtersOpen,
              }"
              name="ChevronDown"
            />
          </MxButton>
        </div>

        <div class="py-6 shrink-0 lg:col-span-4 border-t-8 border-primary-accent lg:border-0">
          <MxButton
            class="flex justify-between w-full h-full"
            theme="inverted"
            @click="toggleLayout"
          >
            {{ translate(layout === 'single' ? 'action.fullProgramme' : 'action.byDate') }}
            <Icon class="text-2xl" name="ArrowRight" width="1.5" />
          </MxButton>
        </div>

        <div
          class="grid w-full lg:col-span-12 -mx-4 px-4 grid-cols-8 lg:grid-cols-12 gap-x-6 overflow-hidden transform transition-[height]"
          :style="{
            height: filtersOpen ? `${filtersReference?.scrollHeight}px` : '0',
          }"
          ref="filtersReference"
        >
          <div class="py-5 col-span-8 lg:col-span-10 lg:col-start-2">
            <div class="py-8">
              <p class="text-4xl">{{ translate('heading.filterDates') }}:</p>

              <div class="flex mt-4 flex-wrap gap-x-4 gap-y-6">
                <div v-for="(date, dateIndex) in festivalDates" class="max-w-[60px] grow">
                  <label
                    class="flex flex-col gap-y-1 relative text-center leading-none"
                    :for="`date-${dateIndex}`"
                  >
                    <MxChoice
                      class="!absolute -top-4 -right-2 text-xl"
                      type="checkbox"
                      :name="`dates-${dateIndex}`"
                      :id="`date-${dateIndex}`"
                      :value="formatDateForSB(date)"
                      :checked="searchData.dateFilters.includes(formatDateForSB(date))"
                      :key="date.toISOString()"
                      @change="handleFilterChange('dateFilters', $event)"
                    />

                    <span>{{ formatDate(date.toISOString(), { weekday: 'short' }) }}</span>

                    <span class="border-t-2 border-primary text-5xl font-secondary">{{
                      formatDate(date.toISOString(), { day: '2-digit' })
                    }}</span>

                    <span class="block px-2 py-0.5 bg-primary">{{
                      formatDate(date.toISOString(), { month: 'short' })
                    }}</span>
                  </label>
                </div>
              </div>
            </div>

            <div
              v-for="filterGroup in filterGroups"
              class="py-8 border-t border-black/25 overflow-hidden"
            >
              <p class="text-4xl">{{ filterGroup.heading }}:</p>

              <div class="flex mt-4 -ml-2 flex-col xs:flex-row xs:flex-wrap gap-x-2 gap-y-1">
                <div v-for="option in filterGroup.options" class="filterItem flex gap-x-2 text-xl">
                  <MxChoice
                    type="checkbox"
                    :name="`filters-${filterGroup.heading}`"
                    :id="`filter-${option.value}`"
                    :label="option.label"
                    :value="option.id"
                    :checked="searchData.filters.includes(option.id)"
                    :key="option.id"
                    @change="handleFilterChange('filters', $event)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>

      <div id="schedule" ref="scheduleReference">
        <client-only v-if="layout === 'single'">
          <div
            class="grid grid-cols-12 gap-x-6"
            :class="{
              container: width < 1024,
            }"
          >
            <p class="lg:hidden col-span-12 text-grey font-medium text-base mb-2">
              {{ scrollLabel }}
            </p>
            <div class="col-span-12 sm:col-span-10 sm:col-start-2">
              <MxCarousel
                v-if="festivalDates?.length"
                class=""
                :configuration="{
                  autoWidth: true,
                  gap: '24px',
                  pagination: false,
                  rewind: false,
                  focus: 0,
                  trimSpace: false,
                  isNavigation: true,
                  start: startingSlide,
                }"
                arrow-position="side"
              >
                <MxCarouselSlide v-for="date in festivalDates" :key="date.toISOString()">
                  <button
                    class="date flex flex-col items-center"
                    :class="{
                      'date--current': currentDate.toISOString() === date.toISOString(),
                    }"
                    type="button"
                    @click="fetchEvents([date])"
                  >
                    <span class="text-7xl font-secondary">{{ date.getDate() }}</span>

                    <span class="text-xl uppercase mb-1">{{
                      formatDate(date.toISOString(), { weekday: 'short' })
                    }}</span>
                  </button>
                </MxCarouselSlide>

                <template #prevArrow>
                  <button
                    class="arrowPrev hidden sm:block splide__arrow splide__arrow--prev absolute top-1/2 transform pointer-events-auto leading-none text-4xl"
                  >
                    <Icon name="ChevronLeft" />
                  </button>
                </template>

                <template #nextArrow>
                  <button
                    class="arrowNext hidden sm:block splide__arrow splide__arrow--next absolute top-1/2 transform pointer-events-auto leading-none text-4xl"
                  >
                    <Icon name="ChevronRight" />
                  </button>
                </template>
              </MxCarousel>
            </div>
          </div>

          <!-- Current date events -->
          <ol
            v-if="currentDateEvents?.length && !isLoading"
            class="mt-4 grid grid-cols-12 gap-x-3 2xl:gap-x-6"
          >
            <li
              v-for="(event, i) in currentDateEvents"
              class="pt-3"
              :class="{
                'col-span-12': event.content?.SyncProduction[0]?.content.priority === 'high',
                'col-span-12 lg:col-span-6':
                  event.content?.SyncProduction[0]?.content.priority === 'medium',
                'col-span-6 lg:col-span-3':
                  event.content?.SyncProduction[0]?.content.priority === 'low' ||
                  event.content?.SyncProduction[0]?.content.priority === 'none' ||
                  !event.content?.SyncProduction[0]?.content.priority,
              }"
              :key="event.uuid"
            >
              <ContentCollectionItem
                class="h-full"
                :itemStyle="itemStyle"
                :priority="event.content?.SyncProduction[0]?.content.priority"
                :figureClass="
                  event.content?.SyncProduction[0]?.content.priority === 'high'
                    ? 'col lg:flex-row aspect-16/9 lg:w-7/12'
                    : event.content?.SyncProduction[0]?.content.priority === 'medium'
                      ? 'flex min-h-[200px] lg:aspect-2/1 flex-row lg:flex-col justify-end'
                      : 'flex aspect-12/13 flex-col justify-end'
                "
                :content="{
                  storyToShow: event,
                }"
                :collapsible="true"
              />
            </li>
          </ol>

          <div
            v-else-if="
              !isLoading && (noEventsFound || !currentDateEvents || currentDateEvents.length === 0)
            "
            class="flex flex-col items-center gap-4 py-16"
          >
            <p class="flex place-content-center text-3xl font-secondary">
              {{ noEventsFoundTitle }}
            </p>
            <MxButton class="text-2xl" layout="solid" @click="refreshPage" size="medium">
              {{ translate('action.try_again') }}
            </MxButton>
            <p class="flex pt-10 place-content-center"> {{ noEventsFoundSubtitle }}</p>
          </div>
        </client-only>

        <!-- Full event listing -->
        <template v-else>
          <dl v-if="Object.keys(sortedTimeline).length" class="space-y-12 lg:space-y-16">
            <template v-for="(events, date) in sortedTimeline">
              <div v-if="events?.length" class="grid grid-cols-12 gap-y-4 gap-x-3 lg:gap-6">
                <dt class="max-2xl:ml-4 col-span-12 text-5xl lg:text-6xl font-secondary">
                  {{
                    formatDate(date as string, { month: 'long', weekday: 'long', day: '2-digit' })
                  }}
                </dt>

                <dd v-for="event in events" :key="event.uuid" class="col-span-6 lg:col-span-3">
                  <ContentCollectionItem
                    class="h-full"
                    :itemStyle="itemStyle"
                    figureClass="flex aspect-3/2 lg:aspect-12/13 flex-col justify-end"
                    :content="{ storyToShow: event }"
                    :collapsible="true"
                  />
                </dd>
              </div>
            </template>
          </dl>

          <div v-else-if="!isLoading" class="flex flex-col items-center gap-4 py-16">
            <p class="flex place-content-center text-3xl font-secondary">
              {{ noEventsFoundTitle }}
            </p>
            <MxButton class="text-2xl" layout="solid" @click="refreshPage" size="medium">
              {{ translate('action.try_again') }}
            </MxButton>
            <p class="flex pt-10 place-content-center"> {{ noEventsFoundSubtitle }}</p>
          </div>
        </template>
      </div>

      <div class="flex mt-12 lg:mt-24 flex-wrap gap-5 w-full max-2xl:container">
        <div
          v-if="isLoading"
          class="flex w-full h-full p-20 absolute inset-0 z-20 items-center justify-center"
          aria-hidden="true"
        >
          <Icon class="sticky bottom-1/2 text-primary text-8xl" name="Spinner" />
        </div>

        <!-- Intersection Observer trigger element for lazy loading-->
        <div ref="loadMoreTrigger" class="w-full h-4"></div>

        <div v-if="isLoadingMore" class="w-full flex justify-center items-center py-4">
          <Icon class="text-primary text-4xl animate-spin" name="Spinner" />
        </div>

        <MxButton href="#schedule" layout="outline" class="max-lg:w-1/2" :background="background">
          <Icon name="Return" width="1.5" />
        </MxButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  filterGroupType,
  ProgrammeScheduleBlock,
  TimelineType,
} from '~/types/ProgrammeScheduleBlock';
import type { ISbStoriesParams, ISbStoryData } from 'storyblok-js-client';
import type { EventProps } from '~/types/Production';
import { formatDate, formatDateForSB, areDatesSameDay } from '~/utils/formatDate';
import { STORY_RELATIONS } from '~/composables/relations';
import {
  updateUrlParams,
  parseUrlFilters,
  filtersToUrlParams,
  findFilterIdsByValues,
} from '~/utils/urlFilters';

const { blok } = defineProps<{
  blok: ProgrammeScheduleBlock;
}>();
const { start, end, background, scrollLabel, noEventsFoundTitle, noEventsFoundSubtitle } = blok;

const runtimeConfig = useRuntimeConfig();
const language = useState<string>('language');
const translate = useTranslation();
const storyBlokApi = useStoryblokApi();

const { storyBlokApiKey, publishState } = runtimeConfig.public;

const isMounted = ref(false);
const width = ref(1920);

const itemStyle = computed(() => {
  return width.value > 1440 ? 'layered' : 'layered-simple';
});

const sacredTimeline = ref<TimelineType>({});
const searchTimeline = ref<TimelineType>({});
const filterGroups = ref<filterGroupType[]>([]);
const filtersOpen = ref<boolean>(false);
const filtersReference = ref<HTMLDivElement | null>(null);
const scheduleReference = ref<HTMLDivElement | null>(null);

const refreshPage = () => {
  window.location.reload();
};

// Lazy loading refs
const observer = ref<IntersectionObserver | null>(null);
const loadMoreTrigger = ref(null);

const layout = ref<'single' | 'full' | 'search'>('single');
const isLoading = ref<boolean>(true);
const showWholeDay = ref<boolean>(false);

let today = new Date();

const festivalDates = computed<Date[]>(() => {
  const dates = [];
  const startDate = new Date(start);
  const endDate = new Date(end);

  for (let date = startDate; date <= endDate; date.setDate(date.getDate() + 1)) {
    dates.push(new Date(date));
  }

  return dates;
});

const getCurrentDateToUse = () => {
  // Makes sure the current date update every day during the festival
  let matched = festivalDates.value[0];
  festivalDates.value.forEach((item) => {
    if (areDatesSameDay(today, item)) {
      matched = item;
      return;
    }
  });

  return matched;
};

const currentDate = ref(getCurrentDateToUse());

const getStartingSlide = () => {
  // Focuses the current day in splide to match the vertical time line border visually
  let index = 0;
  let matchedIndex = 0;
  festivalDates.value.forEach((item) => {
    if (areDatesSameDay(today, item)) {
      matchedIndex = index;
      return;
    }
    index++;
  });

  return matchedIndex;
};

const startingSlide = getStartingSlide();

const makeStoryblokRequest = async (
  params: ISbStoriesParams,
  retryCount = 0,
): Promise<ISbStoryData<EventProps>[]> => {
  const maxRetries = 3;

  try {
    // Uncomment for debugging
    // console.log(`Fetching Storyblok data (attempt ${retryCount + 1}/${maxRetries + 1})`, {
    //   starts_with: params.starts_with,
    //   filter_query: params.filter_query,
    // });

    const events = await storyBlokApi.getAll('cdn/stories', params);

    // Validate the response
    if (!Array.isArray(events)) {
      throw new Error('Storyblok API returned invalid data (not an array)');
    }

    // Verify event data structure for at least one event
    if (events.length > 0) {
      const firstEvent = events[0];
      if (!firstEvent.content || !firstEvent.uuid) {
        console.warn('Event data structure may be invalid', firstEvent);
      }
    }

    return events;
  } catch (error) {
    console.error(`Storyblok API request failed: ${error}`);

    // Implement exponential backoff for retries
    if (retryCount < maxRetries) {
      const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
      await new Promise((resolve) => setTimeout(resolve, delay));
      return makeStoryblokRequest(params, retryCount + 1);
    }

    throw error;
  }
};

const arrangeEventsByPriority = (events: ISbStoryData<EventProps>[]) => {
  const highPriorityEvents = events.filter(
    (event) => event.content?.SyncProduction[0]?.content.priority === 'high',
  );
  const mediumPriorityEvents = events.filter(
    (event) => event.content?.SyncProduction[0]?.content.priority === 'medium',
  );
  const lowPriorityEvents = events.filter(
    (event) => event.content?.SyncProduction[0]?.content.priority === 'low',
  );
  const noPriorityEvents = events.filter(
    (event) =>
      event.content?.SyncProduction[0]?.content.priority === 'none' ||
      !event.content?.SyncProduction[0]?.content.priority,
  );

  return [
    ...highPriorityEvents,
    ...mediumPriorityEvents,
    ...lowPriorityEvents,
    ...noPriorityEvents,
  ];
};

const noEventsFound = ref<boolean>(false);

const currentDateEvents = computed<ISbStoryData<EventProps>[]>(() => {
  const events = sacredTimeline.value[`${formatDateForSB(currentDate.value)}`];
  if (!events || events.length === 0) return [];

  const sortEvents = events.sort((a, b) =>
    a.content.SyncEventStartTime.localeCompare(b.content.SyncEventStartTime),
  );

  return sortEvents.length > 0
    ? arrangeEventsByPriority(sortEvents)
    : events.sort((a, b) =>
        a.content.SyncEventStartTime.localeCompare(b.content.SyncEventStartTime),
      );
});

const sortedTimeline = computed<TimelineType>(() => {
  const currentTimeline = layout.value === 'search' ? searchTimeline.value : sacredTimeline.value;

  // Guard against empty timelines
  if (!currentTimeline || Object.keys(currentTimeline).length === 0) {
    return {};
  }

  return Object.keys(currentTimeline)
    .sort((a, b) => a.localeCompare(b))
    .reduce((accumulator: TimelineType, key) => {
      const events = currentTimeline[key];
      if (events && events.length > 0) {
        accumulator[key] = events.sort((a, b) =>
          a.content.SyncEventStartTime.localeCompare(b.content.SyncEventStartTime),
        );
      } else {
        // Initialize with empty array instead of undefined
        accumulator[key] = [];
      }

      return accumulator;
    }, {});
});

const fetchEvents = async (dates: Date[], useSearch: boolean = false) => {
  const { term, filters } = searchData;

  showWholeDay.value = false;
  isLoading.value = true;

  const arenaIds: string[] = [];
  const categoryIds: string[] = [];

  if (filterGroups.value.length > 0) {
    const arenaGroup = filterGroups.value.find(
      (group) => group.heading.includes('Venue') || group.heading.includes('Arena'),
    );

    const categoryGroup = filterGroups.value.find(
      (group) => group.heading.includes('Categories') || group.heading.includes('Kategori'),
    );

    if (arenaGroup && categoryGroup) {
      filters.forEach((id) => {
        const inArenaGroup = arenaGroup.options.some((option) => option.id === id);
        if (inArenaGroup) {
          arenaIds.push(id);
        }

        const inCategoryGroup = categoryGroup.options.some((option) => option.id === id);
        if (inCategoryGroup) {
          categoryIds.push(id);
        }
      });
    }
  }
  try {
    // Process all dates in parallel for better performance
    const datePromises = dates.map(async (date) => {
      const dateToCheck = new Date(date);
      const dateEnd = new Date(date);
      dateToCheck.setDate(date.getDate());
      dateEnd.setDate(date.getDate() + 1);

      if (sacredTimeline.value[`${formatDateForSB(dateToCheck)}`] && !useSearch) {
        return {
          date: dateToCheck,
          events: sacredTimeline.value[`${formatDateForSB(dateToCheck)}`],
        };
      }

      try {
        const payload: ISbStoriesParams = {
          starts_with: 'data/arrangement',
          filter_query: {
            SyncEventStartTime: {
              gt_date: formatDateForSB(dateToCheck),
            },
            SyncEventEndTime: {
              lt_date: formatDateForSB(dateEnd),
            },
            SyncPrivateEvent: {
              is: false,
            },
          },
          version: publishState as 'draft' | 'published',
          token: storyBlokApiKey,
          resolve_relations: STORY_RELATIONS,
          language: language.value,
          per_page: 100,
          cv: new Date().getTime(),
        };

        if (term) {
          payload.search_term = term;
        }

        if (filters.length) {
          payload.filter_query.SyncCategories = {
            any_in_array: filters.join(','),
          };
        }

        const events = await makeStoryblokRequest(payload);

        // If we have both arena and category filters, apply the AND logic
        let filteredEvents = [...events];

        if (arenaIds.length > 0 && categoryIds.length > 0) {
          filteredEvents = events.filter((event) => {
            if (!event.content.SyncCategories || !Array.isArray(event.content.SyncCategories)) {
              return false;
            }

            const hasArena = arenaIds.some((arenaId) =>
              event.content.SyncCategories.includes(arenaId),
            );

            const hasCategory = categoryIds.some((categoryId) =>
              event.content.SyncCategories.includes(categoryId),
            );

            return hasArena && hasCategory;
          });
        }

        return { date: dateToCheck, events: filteredEvents };
      } catch (error) {
        console.error(`Error fetching events for date ${dateToCheck}:`, error);
        return { date: dateToCheck, events: [] };
      }
    });

    const results = await Promise.all(datePromises);

    // Update the timeline with all results
    results.forEach(({ date, events }) => {
      const formattedDate = formatDateForSB(date);

      if (useSearch) {
        searchTimeline.value = {
          ...searchTimeline.value,
          [formattedDate]: events,
        };
      } else {
        sacredTimeline.value = {
          ...sacredTimeline.value,
          [formattedDate]: events,
        };

        // Only update currentDate if it was explicitly fetched
        if (dates.some((d) => areDatesSameDay(d, date))) {
          currentDate.value = date;
        }
      }
    });

    // Fix: Ensure we have valid data before rendering
    const timelineToCheck = useSearch ? searchTimeline.value : sacredTimeline.value;
    const hasAnyEvents = Object.values(timelineToCheck).some(
      (events) => events && events.length > 0,
    );
    noEventsFound.value = !hasAnyEvents;
  } catch (error) {
    console.error('Error fetching events:', error);
    noEventsFound.value = true;
  } finally {
    isLoading.value = false;
  }
};

export type searchDataType = {
  term: string;
  filters: string[];
  dateFilters: string[];
  categorySlugParam?: string;
  arenaSlugParam?: string;
};

const searchData = reactive<searchDataType>({
  term: '',
  filters: [],
  dateFilters: [],
});

const syncFiltersToUrl = (performSearch = false) => {
  if (!import.meta.client) return;

  // Only sync if filter groups are loaded
  if (filterGroups.value.length === 0) return;

  const params = filtersToUrlParams(searchData, filterGroups.value);
  updateUrlParams(params);
};

const searchEvents = async () => {
  isLoading.value = true;
  searchTimeline.value = {};
  layout.value = 'search';
  noEventsFound.value = false;

  syncFiltersToUrl(true);

  try {
    const filterDates = searchData.dateFilters
      .map((date) => {
        try {
          const dateObj = new Date(date);
          // Check if date is valid
          if (!isNaN(dateObj.getTime())) {
            return dateObj;
          }
          console.warn('Invalid date filter ignored:', date);
          return null;
        } catch (error) {
          console.error('Error parsing date filter:', date, error);
          return null;
        }
      })
      .filter((date) => date !== null) as Date[];

    await fetchEvents(filterDates.length ? filterDates : festivalDates.value, true);

    // Check if we have any results after search
    const hasResults = Object.values(searchTimeline.value).some(
      (events) => events && events.length > 0,
    );
    noEventsFound.value = !hasResults;
  } catch (error) {
    console.error('Error in searchEvents:', error);
    noEventsFound.value = true;
  } finally {
    isLoading.value = false;
  }
};

const toggleLayout = () => {
  searchData.term = '';
  searchData.filters = [];
  searchData.dateFilters = [];
  filtersOpen.value = false;
  layout.value = layout.value === 'single' ? 'full' : 'single';

  // Clear URL parameters
  syncFiltersToUrl();

  if (layout.value === 'full') {
    fetchEvents(festivalDates.value.slice(0, 3));
  }
};

const prepareFilters = async () => {
  const filterData = [
    {
      heading: translate('heading.filterVenue'),
      path: 'data/kategori/arena',
    },
    {
      heading: translate('heading.filterCategories'),
      path: 'data/kategori/arrangement',
    },
  ];

  for (const group of filterData) {
    try {
      const response = await storyBlokApi.getAll('cdn/stories', {
        version: publishState as 'draft' | 'published',
        token: storyBlokApiKey,
        cv: new Date().getTime(),
        starts_with: group.path,
        language: language.value,
        content_type: 'Category',
        per_page: 100,
      });

      const sortedResponse = response.sort((a, b) => {
        const nameA = a.content.name || a.name;
        const nameB = b.content.name || b.name;
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });

      filterGroups.value.push({
        heading: group.heading,
        options: sortedResponse.map((item) => {
          return {
            id: item.uuid,
            label: item.content.name || item.name,
            value: item.slug,
          };
        }),
      });
    } catch (error) {
      console.warn(`failed to fetch "${group}"`);
      console.error(error);
    }
  }

  return filterGroups.value;
};

const handleFilterChange = async (type: 'filters' | 'dateFilters', event: Event) => {
  const targetElement = event.target as HTMLInputElement;
  const indexOfFilter = searchData[type].indexOf(targetElement.value);

  if (indexOfFilter >= 0) {
    searchData[type].splice(indexOfFilter, 1);
  } else {
    searchData[type].push(targetElement.value);
  }

  syncFiltersToUrl(true);

  if (searchData.filters.length || searchData.dateFilters.length) {
    await searchEvents();
  } else {
    layout.value = 'single';
    await fetchEvents([currentDate.value]);
  }

  const scheduleTopPosition = scheduleReference.value?.getBoundingClientRect().top || 0;

  window.scrollTo({
    top: scheduleTopPosition + window.scrollY,
    behavior: 'smooth',
  });
};

const handleBrowserNavigation = () => {
  if (!import.meta.client) return;

  window.addEventListener('popstate', async () => {
    const urlParams = parseUrlFilters();

    searchData.term = urlParams.term || '';
    searchData.dateFilters = Array.isArray(urlParams.dateFilters) ? urlParams.dateFilters : [];

    if (filterGroups.value.length === 0) {
      await prepareFilters();
    }

    const url = new URL(window.location.href);
    const arenaParam = url.searchParams.get('arena');
    const categoryParam = url.searchParams.get('category');

    if (arenaParam || categoryParam) {
      const arenaValues = arenaParam ? arenaParam.split(',') : [];
      const categoryValues = categoryParam ? categoryParam.split(',') : [];
      const allValues = [...arenaValues, ...categoryValues];

      // Find filter IDs by slugs and update searchData.filters
      const filterIds = findFilterIdsByValues(allValues, filterGroups.value);
      searchData.filters = filterIds || [];
    } else {
      searchData.filters = [];
    }

    // Determine layout based on URL parameters
    if (searchData.filters.length || searchData.dateFilters.length || searchData.term) {
      layout.value = 'search';
      // Use a slight delay before search to ensure UI is updated
      setTimeout(() => {
        searchEvents();
      }, 50);
    } else {
      layout.value = 'single';
      fetchEvents([currentDate.value]);
    }
  });
};

const applyUrlParamsToUI = async () => {
  if (!import.meta.client) return;

  const url = new URL(window.location.href);
  const arenaParam = url.searchParams.get('arena');
  const categoryParam = url.searchParams.get('category');

  if ((arenaParam || categoryParam) && filterGroups.value.length > 0) {
    const arenaValues = arenaParam ? arenaParam.split(',') : [];
    const categoryValues = categoryParam ? categoryParam.split(',') : [];
    const allValues = [...arenaValues, ...categoryValues];

    // Find filter IDs by slugs and update searchData.filters
    const filterIds = findFilterIdsByValues(allValues, filterGroups.value);

    if (filterIds && filterIds.length) {
      searchData.filters = filterIds;
      if (isMounted.value) {
        layout.value = 'search';
      }
    }
  }

  if (searchData.filters.length || searchData.dateFilters.length || searchData.term) {
    if (isMounted.value) {
      layout.value = 'search';
      searchEvents();
    }
  }
};

const setupIntersectionObserver = () => {
  if (!import.meta.client) return;

  if (observer.value) {
    observer.value.disconnect();
  }

  observer.value = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting && !isLoading.value) {
        loadMore();
      }
    },
    {
      rootMargin: '200px',
      threshold: 0.1,
    },
  );

  if (loadMoreTrigger.value) {
    observer.value.observe(loadMoreTrigger.value);
  }
};

watch([layout, currentDate], () => {
  nextTick(() => {
    setupIntersectionObserver();
  });
});

onUnmounted(() => {
  if (observer.value) {
    observer.value.disconnect();
  }
});

const isLoadingMore = ref(false);
const hasMoreContent = ref(true);

const loadMore = async () => {
  if (isLoadingMore.value) return; // Prevent multiple simultaneous loads

  isLoadingMore.value = true;

  try {
    if (layout.value === 'single') {
      showWholeDay.value = true;
      hasMoreContent.value = false;
    }

    if (layout.value === 'full') {
      const currentDateIndex = festivalDates.value.findIndex(
        (date) => date.getTime() === currentDate.value.getTime(),
      );

      if (currentDateIndex >= festivalDates.value.length - 4) {
        hasMoreContent.value = false;
      }

      await fetchEvents(festivalDates.value.slice(currentDateIndex + 1, currentDateIndex + 4));
    }
  } catch (error) {
    console.error('Error loading more content:', error);
  } finally {
    isLoadingMore.value = false;
  }
};

watch(
  [layout, () => searchData.term, () => searchData.filters, () => searchData.dateFilters],
  () => {
    hasMoreContent.value = true;
  },
);

onMounted(async () => {
  isMounted.value = true;

  try {
    // Prepare filters first so we can match URL parameters to filter IDs
    await prepareFilters();
    await applyUrlParamsToUI();
    handleBrowserNavigation();

    if (layout.value !== 'search') {
      await fetchEvents([currentDate.value]);
    }

    // Ensure we have events for the current date
    if (
      layout.value === 'single' &&
      (!sacredTimeline.value[formatDateForSB(currentDate.value)] ||
        sacredTimeline.value[formatDateForSB(currentDate.value)].length === 0)
    ) {
      noEventsFound.value = true;
    }
  } catch (error) {
    console.error('Error during component initialization:', error);
    noEventsFound.value = true;
  }

  nextTick(() => {
    width.value = window.innerWidth;

    window.addEventListener('resize', () => {
      width.value = window.innerWidth;
    });

    setupIntersectionObserver();
  });
});
</script>

<style scoped>
.arrowPrev {
  @screen sm {
    right: calc(100% + ((0.5 / 10) * 100%));
  }
}
.arrowNext {
  @screen sm {
    left: calc(100% + ((0.5 / 10) * 100%));
  }
}
.date {
  width: 88px;
  height: 100%;

  opacity: 0.35;

  &:after {
    display: block;
    width: 100%;
    height: 10px;
    margin-top: auto;

    border-bottom: 2px solid theme('colors.primary');
    content: '';
    transition: border-bottom-width 0.15s ease-in;
  }
}
.date--current {
  opacity: 1;

  &:after {
    border-bottom-width: 10px;
  }
}
.timeline {
  --timeline-color: theme('colors.primary.accent');

  &:before,
  &:after {
    border-left: 2px solid var(--timeline-color);
    content: '';
  }
  &:before {
    flex: 0.01 1 auto;
  }
  &:after {
    flex: 1 1 auto;
  }

  @screen lg {
    &:before {
      flex: 1 1 auto;
    }
  }
}
.timeline--highlighted {
  --timeline-color: theme('colors.primary');
}
.filterItem {
  &:before {
    width: 2px;
    height: 0.8em;
    margin-top: 0.4em;

    background-color: rgba(0, 0, 0, 0.15);
    content: '';
  }
}

/* Hide scrollbar for Chrome, Safari and Opera */
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.scrollbar-hide {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}
</style>
